import { Button, Modal } from '@automata/ui';
import { logger, normalizeRouterQuery } from '@automata/utils';
import { useFlag } from '@automata/utils/hooks';
import { useSaveWorkflow } from '@automata/wfb-api';
import CodeIcon from '@mui/icons-material/Code';
import DataObjectIcon from '@mui/icons-material/DataObject';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import WindowIcon from '@mui/icons-material/Window';
import { IconButton, ListItemIcon, Stack, Tooltip } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useRouter } from 'next/router';
import { useCallback, useId, useState } from 'react';
import { useToggle } from 'usehooks-ts';

import { EditPlannerOptionsModal } from './EditPlannerOptionsModal/EditPlannerOptionsModal';
import { EditWorkcellConfigModal } from './EditWorkcellConfigModal';
import { InfoTable } from './InfoTable';
import { SelectWorkcellIdModal } from './SelectWorkcellIdModal';
import { setWorkflowIsSaved } from '../../../features/ui/uiSlice';
import { selectSelectedWorkcellName } from '../../../features/workflow/selectors';
import {
  selectWorkflowJsonToPreview,
  selectWorkflowJsonToSave,
} from '../../../features/workflowJsonSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';

export const DeveloperToolsMenu = () => {
  const dispatch = useAppDispatch();
  const enableDevTools = useFlag('fe.canvas-builder-dev-tools');
  const isPlannerOptionsEnabled = useFlag('fe.planner_options');
  const workflowJsonPreview = useAppSelector(selectWorkflowJsonToPreview);
  const workflowJsonToSave = useAppSelector(selectWorkflowJsonToSave);

  const { query } = useRouter();
  const normalizedQuery = normalizeRouterQuery(query);

  const workflowId = normalizedQuery.id;
  const isNewWorkflow = workflowId ? workflowId.toLowerCase() === 'new' : false;

  const isEmptyWorkflow =
    Object.keys(workflowJsonToSave.workflow.tasks).length === 0;

  const isNewAndEmptyWorkflow = isNewWorkflow && isEmptyWorkflow;

  const id = useId();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [showJsonToPreview, toggleJsonToPreview] = useToggle();
  const [showJsonToSave, toggleJsonToSave] = useToggle();
  const [showInfo, toggleInfo] = useToggle();
  const [showEditWorkcellModal, toggleEditWorkcellModal] = useToggle();
  const [showEditPlannerOptionsModal, toggleEditPlannerOptionsModal] =
    useToggle();
  const [selectWorkcellId, toggleSelectWorkcellId] = useToggle();

  const selectedWorkcellName = useAppSelector(selectSelectedWorkcellName);

  const { saveWorkflow } = useSaveWorkflow({
    onSuccess() {
      logger.info('Workflow saved');
      dispatch(setWorkflowIsSaved(false));
      window.location.reload();
    },
    onError(error) {
      logger.info('Saving workflow failed', error);
    },
  });

  const onClickEditWorkflow = useCallback(() => {
    const json = prompt('Paste in workflow JSON');
    if (json) {
      try {
        const parsed = JSON.parse(json);
        saveWorkflow(parsed, workflowId);
      } catch (e) {
        alert('Invalid JSON');
      }
    }
  }, [saveWorkflow, workflowId]);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  if (!enableDevTools) return null;

  return (
    <>
      <IconButton onClick={handleOpen} aria-label="Manage workflow">
        <CodeIcon />
      </IconButton>
      <Menu
        id={id}
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': `${id}-button`,
        }}
      >
        <MenuItem onClick={onClickEditWorkflow} disabled={workflowId === 'new'}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          Edit workflow
        </MenuItem>
        <MenuItem onClick={toggleJsonToSave}>
          <ListItemIcon>
            <DataObjectIcon />
          </ListItemIcon>
          View workflow JSON
        </MenuItem>
        <MenuItem onClick={toggleJsonToPreview}>
          <ListItemIcon>
            <DataObjectIcon />
          </ListItemIcon>
          View workflow JSON for preview
        </MenuItem>
        <MenuItem onClick={toggleInfo}>
          <ListItemIcon>
            <InfoIcon />
          </ListItemIcon>
          Workflow info
        </MenuItem>
        <MenuItem onClick={toggleEditWorkcellModal}>
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          Edit workcell instruments
        </MenuItem>
        <Tooltip
          title={
            !isNewAndEmptyWorkflow
              ? `Only available for new, empty workflows. Current workcell is "${selectedWorkcellName}".`
              : ''
          }
        >
          <div>
            <MenuItem
              onClick={toggleSelectWorkcellId}
              disabled={!isNewAndEmptyWorkflow}
            >
              <ListItemIcon>
                <WindowIcon />
              </ListItemIcon>
              Select workcell
            </MenuItem>
          </div>
        </Tooltip>
        {isPlannerOptionsEnabled && (
          <MenuItem onClick={toggleEditPlannerOptionsModal}>
            <ListItemIcon>
              <EditIcon />
            </ListItemIcon>
            Edit planner options
          </MenuItem>
        )}
      </Menu>
      {showJsonToSave && (
        <Modal
          open={showJsonToSave}
          onClose={toggleJsonToSave}
          title="Workflow JSON"
        >
          <Stack>
            <pre>{JSON.stringify(workflowJsonToSave, null, 2)}</pre>
            <Button
              variant="outlined"
              onClick={toggleJsonToSave}
              type="button"
              sx={{ flexGrow: 1 }}
            >
              Close
            </Button>
          </Stack>
        </Modal>
      )}
      {showJsonToPreview && (
        <Modal
          open={showJsonToPreview}
          onClose={toggleJsonToPreview}
          title="Workflow JSON for preview"
        >
          <Stack>
            <pre>{JSON.stringify(workflowJsonPreview, null, 2)}</pre>
            <Button
              variant="outlined"
              onClick={toggleJsonToPreview}
              type="button"
              sx={{ flexGrow: 1 }}
            >
              Close
            </Button>
          </Stack>
        </Modal>
      )}
      {showInfo && <InfoTable open={showInfo} onClose={toggleInfo} />}
      {showEditWorkcellModal && (
        <EditWorkcellConfigModal onClose={toggleEditWorkcellModal} />
      )}
      {selectWorkcellId && (
        <SelectWorkcellIdModal onClose={toggleSelectWorkcellId} />
      )}
      {showEditPlannerOptionsModal && (
        <EditPlannerOptionsModal onClose={toggleEditPlannerOptionsModal} />
      )}
    </>
  );
};
